import React, { useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "src/components/ui/Card";
import { Button } from "src/components/ui/Button";
import { Edit, Trophy, Award, Plus, Trash2 } from "lucide-react";
import { Modal } from "src/components/ui/Modal";
import { FormInput } from "src/components/ui/FormInput";
import { TutorExtendedProfileData, TutorSectionType } from "src/interfaces/TutorProfile";
import { useTutorProfileEditStore } from "src/store/tutorProfileEditStore";

interface TutorAchievementsSectionProps {
  profileData: TutorExtendedProfileData;
  editData: Partial<TutorExtendedProfileData>;
  activeSectionEdit: TutorSectionType | null;
  setActiveSectionEdit: (section: TutorSectionType | null) => void;
  updateEditData: (field: string, value: any) => void;
  updateProfile: (data: Partial<TutorExtendedProfileData>) => Promise<void>;
  isModified?: boolean;
}

const TutorAchievementsSection: React.FC<TutorAchievementsSectionProps> = ({
  profileData,
  editData,
  activeSectionEdit,
  setActiveSectionEdit,
  updateEditData,
  updateProfile,
  isModified = false
}) => {
  const {
    editingAchievements,
    setEditingAchievements,
    addAchievement,
    removeAchievement,
    updateAchievement
  } = useTutorProfileEditStore();

  // Sync achievements with updateEditData whenever editingAchievements changes
  useEffect(() => {
    if (activeSectionEdit === 'achievements') {
      updateEditData("achievements", editingAchievements);
    }
  }, [editingAchievements, activeSectionEdit, updateEditData]);
  return (
    <>
      {/* Achievements Section */}
      <Card className={isModified ? "ring-2 ring-blue-200 bg-blue-50/30" : ""}>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Achievements</CardTitle>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 p-0 rounded-full hover:bg-gray-100"
            onClick={() => {
              const currentAchievements = editData.achievements || profileData.achievements;
              setEditingAchievements(currentAchievements);
              setActiveSectionEdit('achievements');
            }}
            aria-label="Edit Achievements"
          >
            <Edit className="h-4 w-4 text-gray-500" />
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {profileData.achievements && profileData.achievements.length > 0 ? (
              profileData.achievements.map((achievement, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    {index % 2 === 0 ? (
                      <Trophy className="h-6 w-6 text-amber-500" />
                    ) : (
                      <Award className="h-6 w-6 text-amber-500" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-700">{achievement}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500">No achievements specified</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Achievements Edit Modal */}
      <Modal
        isOpen={activeSectionEdit === 'achievements'}
        onClose={() => setActiveSectionEdit(null)}
        title="Edit Achievements"
      >
      <div className="space-y-4">
          <div className="text-sm text-gray-500 mb-4">
            Add your professional achievements, awards, certifications, and recognitions.
          </div>
          
          <div className="flex items-center justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addAchievement}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Achievement
            </Button>
          </div>

          {editingAchievements.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Trophy className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No achievements added yet</p>
              <p className="text-sm">Click "Add Achievement" to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {editingAchievements.map((achievement, index) => (
                <div key={index}>
                  {/* Label above the container */}
                  <div className="mb-1 text-sm font-semibold text-gray-700">Achievement {index + 1}</div>
                  <div className="flex items-start space-x-3 p-3 border rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-1">
                      {index % 2 === 0 ? (
                        <Trophy className="h-4 w-4 text-amber-500" />
                      ) : (
                        <Award className="h-4 w-4 text-amber-500" />
                      )}
                    </div>
                    <div className="flex-1">
                      <FormInput
                        label=""
                        id={`achievement-${index}`}
                        type="textarea"
                        value={achievement}
                        onChange={(e) => updateAchievement(index, e.target.value)}
                        placeholder="Describe your achievement, award, or recognition..."
                        rows={2}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeAchievement(index)}
                      className="text-red-600 hover:text-red-700 flex-shrink-0 mt-6"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setActiveSectionEdit(null)}>
              Cancel
            </Button>
            <Button onClick={() => {
              updateProfile(editData);
              setActiveSectionEdit(null);
            }}>
              Save
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default TutorAchievementsSection;